import { useEffect, useState } from 'react'
import { api } from './api/client'
import { ProfileList } from './components/ProfileList'
import { CreateProfile } from './components/CreateProfile'
import { BrowserView } from './components/BrowserView'
import { Settings } from './components/Settings'
import type { HealthStatus, Profile, RunningInstance } from './types/profile'

type Tab = 'list' | 'create' | 'running' | 'settings'

export function App() {
  const [tab, setTab] = useState<Tab>('list')
  const [health, setHealth] = useState<HealthStatus | null>(null)
  const [healthError, setHealthError] = useState<string | null>(null)
  const [profiles, setProfiles] = useState<Profile[]>([])
  const [running, setRunning] = useState<RunningInstance[]>([])
  const [selectedRunning, setSelectedRunning] = useState<RunningInstance | null>(null)
  const [loading, setLoading] = useState(false)

  const apiBase = api.getApiBase()

  async function refreshAll() {
    if (!apiBase) return
    setLoading(true)
    setHealthError(null)
    try {
      const [h, p, r] = await Promise.all([
        api.health(),
        api.listProfiles(),
        api.listRunning(),
      ])
      setHealth(h)
      setProfiles(p)
      setRunning(r)
    } catch (e) {
      setHealth(null)
      setHealthError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (apiBase) refreshAll()
  }, [apiBase])

  if (!apiBase) {
    return <Settings onSaved={() => window.location.reload()} />
  }

  return (
    <div className="app">
      <header className="app-header">
        <h1>
          <span className="logo">🛡️</span> 指纹浏览器
        </h1>
        <nav>
          <button
            className={tab === 'list' ? 'active' : ''}
            onClick={() => setTab('list')}
          >
            Profiles ({profiles.length})
          </button>
          <button
            className={tab === 'create' ? 'active' : ''}
            onClick={() => setTab('create')}
          >
            新建
          </button>
          <button
            className={tab === 'running' ? 'active' : ''}
            onClick={() => setTab('running')}
          >
            运行中 ({running.length})
          </button>
          <button
            className={tab === 'settings' ? 'active' : ''}
            onClick={() => setTab('settings')}
          >
            设置
          </button>
        </nav>
      </header>

      <section className="app-status">
        {loading && <span className="status loading">刷新中…</span>}
        {health && (
          <span className="status ok">
            后端 v{health.version} · 内核 {health.kernelVersion ?? health.kernelPath}
            {' · '}
            已运行 {Math.floor(health.uptimeSeconds / 60)} 分钟
          </span>
        )}
        {healthError && (
          <span className="status error">后端不可达：{healthError}</span>
        )}
        <button onClick={refreshAll} disabled={loading}>
          刷新
        </button>
      </section>

      <main>
        {tab === 'list' && (
          <ProfileList
            profiles={profiles}
            onLaunched={refreshAll}
            onDeleted={refreshAll}
          />
        )}
        {tab === 'create' && (
          <CreateProfile onCreated={() => { setTab('list'); refreshAll() }} />
        )}
        {tab === 'running' && (
          <BrowserView
            instances={running}
            profiles={profiles}
            selected={selectedRunning}
            onSelect={setSelectedRunning}
            onStopped={refreshAll}
          />
        )}
        {tab === 'settings' && <Settings onSaved={() => window.location.reload()} />}
      </main>
    </div>
  )
}